import TooltipInfo from '@components/TooltipInfo'
import { Button } from 'antd'
import { SizeType } from 'antd/lib/config-provider/SizeContext'
import cx from 'classnames'
import React, { memo } from 'react'

import { useActivitySubject } from '../../../activity/ActivitySubjectContext'
import { runTrackedAction } from '../../../activity/trackedRun'
import InputHint from '../InputHint'

enum Colors {
  default,
  red,
  blue,
  green,
  black,
  yellow,
}

type ColorType = keyof typeof Colors

type Props = {
  children?: React.ReactNode
  className?: string
  color?: ColorType
  size?: SizeType
  rounded?: boolean
  circle?: boolean
  outline?: boolean
  dashed?: boolean
  disabled?: boolean
  tooltip?: React.ReactElement
  onClick?: (e: any) => void
  loading?: boolean
  title?: string
  /**
   * The catalog name of the action this button starts (src/activity/catalog), e.g.
   * "Ime.Appointment.Save". The click is recorded in the history of the nearest
   * ActivitySubjectProvider's record; a promise returned by onClick keeps the action open until
   * it settles. Without a provider, or for a name not in the catalog, nothing is recorded.
   */
  trackAction?: string
  [rest: string]: any
}

const AppButtonBase = ({
  children = null,
  className = '',
  color = 'default',
  rounded = true,
  circle = false,
  outline = false,
  dashed = false,
  loading = false,
  disabled = false,
  size = 'middle',
  tooltip = null,
  onClick = (e: any) => {},
  title = null,
  ...rest
}: Props) => {
  const colorClassName = () => {
    if (outline) {
      return `${color}-outline`
    }
    if (dashed) {
      return `${color}-outline-dashed`
    }
    return color
  }

  const getShapeClassName = () => {
    if (circle) {
      return 'circle'
    }

    if (rounded) {
      return 'round'
    }
  }

  const colorText = colorClassName()

  const handleClick = (e: any) => {
    if (disabled) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    if (onClick) {
      onClick(e)
    }
  }

  return (
    <Button
      shape={getShapeClassName()}
      loading={loading}
      onClick={handleClick}
      className={cx([
        'inline-flex items-center justify-center',
        disabled ? 'bg-grey-400 cursor-not-allowed' : 'cursor-pointer',
        className,
        `btn-${colorText}`,
      ])}
      size={size}
      title={title}
      {...rest}
    >
      {children}
      {tooltip && <TooltipInfo iconColor="text-white" text={tooltip} />}
      <InputHint text={rest.hint} />
    </Button>
  )
}

/** An AppButton with trackAction: runs onClick as that action on the provider's subject. */
const TrackedAppButton = ({ trackAction, onClick, ...props }: Props) => {
  const subject = useActivitySubject()

  const handleClick = (e: any) =>
    runTrackedAction(trackAction, subject, () => onClick?.(e))

  return <AppButtonBase {...props} onClick={handleClick} />
}

export const AppButton = memo(({ trackAction, ...props }: Props) =>
  trackAction ? (
    <TrackedAppButton trackAction={trackAction} {...props} />
  ) : (
    <AppButtonBase {...props} />
  )
)
